import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';

import Button from '@/components/Button';
import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import DownloadModal from '@/components/Modals/DownloadModal/DownloadModal';
import Modal from '@/components/Modals/Modal';
import { getCurrencySymbol } from '@/constants/countries';
import useResponsive from '@/hooks/useResponsive';
import { RootState } from '@/redux/store';

import { getBgStatus, getLbColorStatus, getStatusLabel } from '../../../constants';
import useDetailsModalPlugins from './useDetailsModalPlugins';

interface DetailsModalPluginsProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  clientId: string;
}

interface PairTitleProps {
  title: string;
  value?: string;
  isBold?: boolean;
  iconTitle?: React.ReactNode;
  iconValue?: React.ReactNode;
  isPrice?: boolean;
  isGreen?: boolean;
  isTitleGreen?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
}

const DetailsModalPlugins = ({ isOpen, onClose, orderId, clientId }: DetailsModalPluginsProps) => {
  const isTablet = useResponsive('up', 'lg');
  //   const isMobile = useResponsive('down', 'sm');
  const theme = useTheme();

  const hook = useDetailsModalPlugins({ orderId, clientId, isOpen });
  const cellStyleHeader = {
    padding: '12px',
    fontWeight: 600,
    color: theme.palette.secondary.main,
    whiteSpace: 'nowrap' as const,
  };

  const cellStyle = {
    padding: '10px',
    color: '#444',
    backgroundColor: '#fafafa',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Modal isOpen={isOpen} title="Detalle de orden" onClose={onClose} maxWidth="xl">
      {hook.loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : hook.error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography color="error">
            Error al cargar los detalles de la orden. Por favor, intenta nuevamente.
          </Typography>
        </Box>
      ) : hook.orderData ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: isTablet ? '0' : '0 !important',
            }}
          >
            <Box display="flex" gap={2} width="100%" alignItems="stretch">
              <Box flex={3}>
                <Typography
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  textAlign="center"
                  mb={1}
                >
                  Mercado Libre
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                  <Grid size={{ xxs: 12, sm: 4 }}>
                    <PairTitle
                      title="Fecha de compra"
                      value={
                        hook.orderData?.createdAt
                          ? format(new Date(hook.orderData.createdAt), 'dd/MM/yyyy')
                          : format(new Date(), 'dd/MM/yyyy')
                      }
                    />
                  </Grid>
                  <Grid size={{ xxs: 12, sm: 4 }}>
                    <PairTitle
                      title="ID orden"
                      value={hook.orderData?.orderId ?? ''}
                      isTitleGreen
                      isBold
                    />
                  </Grid>
                  <Grid size={{ xxs: 12, sm: 4 }}>
                    <PairTitle title="Estatus">
                      <StatusChip
                        status={getStatusLabel(hook.orderData?.statusOrder)}
                        color={getLbColorStatus(hook.orderData?.statusOrder)}
                        bg={getBgStatus(hook.orderData?.statusOrder)}
                      />
                    </PairTitle>
                  </Grid>
                  {(hook.orderData?.orderData.logisticMode ||
                    hook.orderData?.orderData.logisticType) && (
                    <>
                      <Grid size={{ xxs: 12, sm: 6 }}>
                        <PairTitle
                          title="Modo logístico"
                          value={hook.orderData.orderData.logisticMode ?? '-'}
                        />
                      </Grid>
                      <Grid size={{ xxs: 12, sm: 6 }}>
                        <PairTitle
                          title="Tipo logístico"
                          value={hook.orderData.orderData.logisticType ?? '-'}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box flex={1} display="flex" flexDirection="column" alignItems="center">
                <Typography
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  textAlign="center"
                  mb={1}
                >
                  99minutos
                </Typography>
                <PairTitle
                  title="Nº de seguimiento"
                  value={
                    hook.orderData?.trackingId && hook.orderData.trackingId.length > 0
                      ? hook.orderData.trackingId
                      : 'Sin asignar'
                  }
                />
              </Box>
            </Box>

            <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

            <Grid container spacing={2} width={1} justifyContent="center">
              <Grid size={{ xxs: 12, md: 6 }}>
                <PairTitle
                  title="Dirección de origen"
                  value={
                    hook.orderData?.orderData?.originAddressData?.address
                      ? `${hook.orderData.orderData.originAddressData.address}, CP: ${hook.orderData.orderData.originAddressData.zipCode || 'N/A'}`
                      : 'No disponible'
                  }
                />
              </Grid>
              <Grid size={{ xxs: 12, md: 6 }}>
                <PairTitle
                  title="Dirección de destino"
                  value={
                    hook.orderData?.orderData?.destinationAddressData?.address
                      ? `${hook.orderData.orderData.destinationAddressData.address}, CP: ${hook.orderData.orderData.destinationAddressData.zipCode ?? 'N/A'}`
                      : 'No disponible'
                  }
                />
              </Grid>
            </Grid>

            <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

            {hook.orderData?.orderData.items?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color={theme.palette.primary.main}
                  align="center"
                  mt={2}
                >
                  Producto(s)
                </Typography>

                <Box
                  mt={2}
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    paddingBottom: '1rem',
                  }}
                >
                  <Box
                    component="table"
                    sx={{
                      minWidth: 570,
                      width: '100%',
                      borderCollapse: 'collapse',
                      textAlign: 'center',
                      fontSize: '0.95rem',
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#f9f9f9' }}>
                        <th style={cellStyleHeader}>Nombre del producto</th>
                        <th style={cellStyleHeader}>Valor de producto</th>
                        <th style={cellStyleHeader}>Moneda</th>
                        <th style={cellStyleHeader}>SKU</th>
                        <th style={cellStyleHeader}>Cantidad de productos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hook.orderData.orderData.items.map((item, index) => (
                        <tr key={index} style={{ borderTop: '1px solid #eee' }}>
                          <td style={cellStyle}>{item.name}</td>
                          <td style={cellStyle}>
                            ${' '}
                            {item.price >= 1000
                              ? item.price.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: hook.orderData?.orderData.currency ?? 'MXN',
                                })
                              : item.price}
                          </td>
                          <td style={cellStyle}>{hook.orderData?.orderData.currency ?? ''}</td>
                          <td style={cellStyle}>{item.sku}</td>
                          <td style={cellStyle}>{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              </>
            )}

            <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

            <Grid container spacing={2} width={1} justifyContent="center" alignItems="center">
              <Grid size={{ xxs: 12, md: 4 }}>
                <PairTitle
                  title="Moneda"
                  value={hook.orderData?.orderData.currency ?? ''}
                  isBold
                  isGreen
                />
              </Grid>
              <Grid size={{ xxs: 12, md: 4 }}>
                <PairTitle
                  title="Costo de orden"
                  value={`$ ${
                    parseFloat(hook.orderData?.orderData.amount) >= 1000
                      ? parseFloat(hook.orderData?.orderData.amount).toLocaleString('en-US')
                      : (hook.orderData?.orderData.amount ?? 0)
                  }`}
                  isBold
                  isGreen
                />
              </Grid>
              <Grid size={{ xxs: 12, md: 4 }} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  onClick={hook.handleDownloadModal}
                  disabled={hook.orderData?.status !== 'created' || !hook.orderData?.trackingId}
                >
                  Descargar etiqueta
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      ) : null}
      <DownloadModal
        amount
        isOpen={hook.downloadModal}
        onClose={hook.handleDownloadModal}
        downloadLabels={hook.downloadLabels}
        trackingIds={[hook.orderData?.trackingId || '']}
      />
    </Modal>
  );
};

export default DetailsModalPlugins;

const PairTitle = ({
  title,
  value,
  isBold,
  isPrice,
  children,
  isGreen,
  isTitleGreen,
  icon,
}: PairTitleProps) => {
  const theme = useTheme();
  const { profile } = useSelector((state: RootState) => state.parcel);
  const currencySymbol = getCurrencySymbol(profile.data?.country);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} textAlign="center">
      <Box display="flex" alignItems="center" sx={{ margin: '0 auto' }}>
        <Typography
          variant="body1"
          fontWeight={theme.typography.fontWeightMedium}
          sx={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            color: isTitleGreen ? theme.palette.primary.main : 'inherit',
          }}
        >
          {title} {icon ? icon : ''}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" sx={{ margin: '0 auto' }}>
        {children}
        {isPrice ? (
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            {currencySymbol} {value}
          </Typography>
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontWeight: isBold ? 'bold' : 'normal',
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              color: isGreen ? theme.palette.primary.main : theme.palette.grey[800],
              textAlign: 'center',
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
